import React, { useState } from 'react';
import { Calendar, Users, Bed, User, Mail, Phone, ArrowRight, ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Calendar as CalendarComponent } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Input } from './ui/input';
import { format } from 'date-fns';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const BookingWidget = ({ variant = 'default' }) => {
  const [step, setStep] = useState(1);
  const [checkIn, setCheckIn] = useState(null);
  const [checkOut, setCheckOut] = useState(null);
  const [guests, setGuests] = useState('2');
  const [roomType, setRoomType] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleNextStep = () => {
    if (!checkIn || !checkOut || !roomType) {
      toast.error('Please fill in all required fields');
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name || !email || !phone) {
      toast.error('Please fill in all personal information');
      return;
    }

    if (!email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const bookingData = {
        checkIn: format(checkIn, 'yyyy-MM-dd'),
        checkOut: format(checkOut, 'yyyy-MM-dd'),
        guests,
        roomType,
        name,
        email,
        phone
      };

      const response = await axios.post(`${API}/booking`, bookingData);
      toast.success(response.data.message || 'Booking request sent successfully!');
      
      // Reset form
      setStep(1);
      setCheckIn(null);
      setCheckOut(null);
      setGuests('2');
      setRoomType('');
      setName('');
      setEmail('');
      setPhone('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const widgetClass = variant === 'hero'
    ? 'bg-white/95 backdrop-blur-sm shadow-2xl rounded-xl p-6 md:p-8 max-w-5xl w-full mx-auto'
    : 'bg-white shadow-lg rounded-lg p-6';

  return (
    <div className={widgetClass}>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-2xl font-bold" style={{ color: '#102854' }}>
          {step === 1 ? 'Book Your Stay' : 'Your Information'}
        </h3>
        {step === 2 && (
          <Button
            variant="ghost"
            onClick={() => setStep(1)}
            className="text-sm"
            style={{ color: '#102854' }}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back
          </Button>
        )}
      </div>

      <form onSubmit={step === 2 ? handleSubmit : (e) => e.preventDefault()}>
        {step === 1 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              {/* Check-in Date */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#102854' }}>
                  Check-in
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      style={{ borderColor: '#102854' }}
                    >
                      <Calendar className="mr-2 h-4 w-4" style={{ color: '#C9A961' }} />
                      {checkIn ? format(checkIn, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={checkIn}
                      onSelect={setCheckIn}
                      initialFocus
                      disabled={(date) => date < new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Check-out Date */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#102854' }}>
                  Check-out
                </label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                      style={{ borderColor: '#102854' }}
                    >
                      <Calendar className="mr-2 h-4 w-4" style={{ color: '#C9A961' }} />
                      {checkOut ? format(checkOut, 'PPP') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={checkOut}
                      onSelect={setCheckOut}
                      initialFocus
                      disabled={(date) => date < new Date() || (checkIn && date <= checkIn)}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Number of Guests */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#102854' }}>
                  Guests
                </label>
                <Select value={guests} onValueChange={setGuests}>
                  <SelectTrigger className="w-full" style={{ borderColor: '#102854' }}>
                    <Users className="mr-2 h-4 w-4" style={{ color: '#C9A961' }} />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 Guest</SelectItem>
                    <SelectItem value="2">2 Guests</SelectItem>
                    <SelectItem value="3">3 Guests</SelectItem>
                    <SelectItem value="4">4 Guests</SelectItem>
                    <SelectItem value="5">5+ Guests</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Room Type */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#102854' }}>
                  Room Type
                </label>
                <Select value={roomType} onValueChange={setRoomType}>
                  <SelectTrigger className="w-full" style={{ borderColor: '#102854' }}>
                    <Bed className="mr-2 h-4 w-4" style={{ color: '#C9A961' }} />
                    <SelectValue placeholder="Select room" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard">Standard Room</SelectItem>
                    <SelectItem value="executive">Executive Room</SelectItem>
                    <SelectItem value="royal">Royal Suite</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Button
              type="button"
              onClick={handleNextStep}
              className="w-full font-semibold text-base py-6 transition-all hover:scale-[1.02]"
              style={{
                backgroundColor: '#C9A961',
                color: '#ffffff'
              }}
            >
              Continue
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </>
        )}

        {step === 2 && (
          <>
            <div className="space-y-4 mb-6">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#102854' }}>
                  Full Name <span style={{ color: '#C9A961' }}>*</span>
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#C9A961' }} />
                  <Input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Enter your full name"
                    className="pl-10"
                    style={{ borderColor: '#102854' }}
                    required
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#102854' }}>
                  Email Address <span style={{ color: '#C9A961' }}>*</span>
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#C9A961' }} />
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your.email@example.com"
                    className="pl-10"
                    style={{ borderColor: '#102854' }}
                    required
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#102854' }}>
                  Phone Number <span style={{ color: '#C9A961' }}>*</span>
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4" style={{ color: '#C9A961' }} />
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    className="pl-10"
                    style={{ borderColor: '#102854' }}
                    required
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full font-semibold text-base py-6 transition-all hover:scale-[1.02]"
              style={{
                backgroundColor: '#C9A961',
                color: '#ffffff'
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Sending Request...' : 'Submit Booking Request'}
            </Button>
          </>
        )}
      </form>
    </div>
  );
};